import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconSettings } from '../icons';
import { useSession } from '../session';

/**
 * What is actually hooked up — and what is not.
 *
 * Every service here needs an account somebody has to go and create, so the interesting
 * state is OFF. A settings page that lists integrations without saying which of them are
 * inert is lying by omission: the owner assumes their invoices are reaching QuickBooks
 * because there is a QuickBooks row on the screen.
 *
 * QuickBooks gets the most room because it is the one with a QUEUE behind it. Work has been
 * piling up in the outbox since the first invoice was issued, and a shop needs to see both
 * that it is waiting and that nothing is stuck — with the reason, and a way to try again.
 * A parked row with no retry is just a grave.
 */

function Row({
  name,
  what,
  on,
  children,
}: {
  name: string;
  what: string;
  on: 'on' | 'off' | 'warn';
  children?: React.ReactNode;
}) {
  return (
    <li className="border-t border-border py-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1 text-sm font-medium">{name}</span>
        {on === 'on' ? <StatusBadge tone="success">On</StatusBadge> : null}
        {on === 'warn' ? <StatusBadge tone="warn">Needs attention</StatusBadge> : null}
        {on === 'off' ? <StatusBadge tone="neutral">Not set up</StatusBadge> : null}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{what}</p>
      {children}
    </li>
  );
}

export function ConnectedServicesCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const [error, setError] = useState<string | null>(null);
  const q = useQuery(trpc.integrations.status.queryOptions());

  const retry = useMutation(
    trpc.integrations.retryStuck.mutationOptions({
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );

  /**
   * This card already branched three ways and was one of only two that did — but in its own
   * words, and with no offline state, so a shop with no signal sat under a skeleton that
   * would never resolve. Same contract as the rest of the family now.
   *
   * The test email it once had moved to Settings → Email with MAP 249 ticket 07
   * (`SendTestEmail`), with its delivery-failure copy.
   */
  const state = shownState(q, q.data);
  if (!q.data) {
    return (
      <SectionCard id={id} title="Connected services">
        <UnresolvedState
          state={unresolved(state)}
          what="what's connected"
          icon={IconSettings}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </SectionCard>
    );
  }

  const { quickbooks: qb, cardPayments, email, textMessages } = q.data;
  const queued = qb.waiting;

  return (
    <SectionCard id={id} title="Connected services">
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <ul>
        <Row
          name="QuickBooks Online"
          on={qb.connected ? (qb.stuck.length ? 'warn' : 'on') : 'off'}
          what={
            !qb.connected
              ? 'Your invoices and payments are being kept in order here, ready to send across the moment you connect it. Nothing is lost in the meantime.'
              : qb.synced === 0
                ? 'Connected. Nothing has gone across yet.'
                : `${qb.synced.toLocaleString()} invoices and payments have gone across.`
          }
        >
          {queued > 0 ? (
            <p className="mt-1 text-sm">
              <strong className="tabular-nums">{queued.toLocaleString()}</strong> waiting
              to go across.
            </p>
          ) : null}

          {qb.stuck.length ? (
            <div className="mt-2">
              <Notice tone="warn">
                {qb.stuck.length === 1
                  ? 'One thing could not be sent to QuickBooks.'
                  : `${qb.stuck.length} things could not be sent to QuickBooks.`}{' '}
                They stay here until they go across — nothing has been lost.
              </Notice>
              <ul className="mt-2 space-y-1">
                {qb.stuck.map((s) => (
                  <li key={s.id} className="text-sm">
                    <span className="font-medium">{s.label}</span>
                    <span className="text-muted-foreground"> — {s.reason}</span>
                  </li>
                ))}
              </ul>
              {role === 'owner' ? (
                <Button
                  className="mt-2"
                  onClick={() => retry.mutate()}
                  disabled={retry.isPending}
                >
                  {retry.isPending ? 'Trying again…' : 'Try these again'}
                </Button>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Ask the owner to try these again once it&apos;s sorted out.
                </p>
              )}
            </div>
          ) : null}
        </Row>

        <Row
          name="Card payments"
          on={cardPayments.on ? 'on' : cardPayments.needsAttention ? 'warn' : 'off'}
          what={
            cardPayments.on
              ? 'Customers can pay their invoice online, and you can take a card at the door.'
              : cardPayments.needsAttention
                ? 'Connected, but not able to take a card yet — Stripe still needs something from you.'
                : "Not set up, so there's no pay-online link on your invoices. You can still record a cheque, cash or a card you took elsewhere."
          }
        />

        <Row
          name="Email"
          on={email.on ? (email.recentFailures ? 'warn' : 'on') : 'off'}
          what={
            email.on
              ? 'Set up for quotes, invoices and payment reminders.'
              : // NOT "download the PDF". An invoice PDF's Total is the invoice total: it
                // carries no late fee and no record of anything already paid, so recommending
                // it in the one state where a correct one cannot be emailed is how somebody
                // gets chased for money they already sent. The customer link renders live.
                'Not set up. Quotes and invoices still have a link you can send yourself — nothing is stuck.'
          }
        >
          {/* This line was unreachable until 017: it counts `status = 'failed'` reminder
              rows, and no code path could write that status — every bounce was recorded as
              'skipped'. The number was structurally zero on every deployment. "emails"
              rather than "reminders" now, because the count covers both unattended sweeps:
              the payment chase and the quote follow-up. */}
          {/* ⚠️ "weren't confirmed", not "failed to send", and "may not have heard" rather
              than "did not hear". This is an AGGREGATE: `status = 'failed'` covers refusals,
              rate limits AND `unreachable` — the one that may well have arrived. A count
              cannot ask `knownNotSent` about its members, so the sentence has to be true of
              the weakest one in it. The adversary caught this after the per-row copy was
              already fixed; an aggregate is where an UNKNOWN hides best. */}
          {email.recentFailures > 0 ? (
            <p className="mt-1 text-sm">
              <strong className="tabular-nums">{email.recentFailures}</strong> automatic{' '}
              {email.recentFailures === 1 ? 'email' : 'emails'}{' '}
              {email.recentFailures === 1 ? "wasn't" : "weren't"} confirmed as sent in the
              last week. Customers you were chasing may not have heard from you.
            </p>
          ) : null}

          {/* The test email, the reply address and everything else about email live in
              Settings → Email (MAP 249 ticket 07); this row keeps whether it is on. */}
          <Link
            to="/settings"
            hash="email"
            className="mt-1 inline-flex min-h-9 items-center text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
          >
            Email settings
          </Link>
        </Row>

        <Row
          name="Text messages"
          on={textMessages.on ? 'on' : 'off'}
          what="Not built yet. Reminders and on-my-way texts are coming; a phone number has to be registered with the carriers first, which takes a few weeks."
        />
      </ul>
    </SectionCard>
  );
}
